import { useCallback, useEffect, useRef, useState } from "react";

interface UseStickToBottomReturn {
  scrollRef: (node: HTMLDivElement | null) => void;
  contentRef: (node: HTMLDivElement | null) => void;
  scrollToBottom: () => void;
}

/**
 * Keeps a chat stream pinned to its latest message.
 *
 * Follows the content's size rather than the message list, so anything that
 * grows the stream after the render that added it (a shared image finishing
 * loading, a capture card, the thinking indicator) still lands in view.
 * Scrolling up to read history releases the pin; returning to the bottom or
 * calling `scrollToBottom` restores it.
 *
 * Both refs are callback refs because chat surfaces mount their scroll
 * container only once there is a first message.
 */
export const useStickToBottom = (threshold = 80): UseStickToBottomReturn => {
  const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
  const [contentNode, setContentNode] = useState<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);

  useEffect(() => {
    if (!scrollNode) return;
    pinnedRef.current = true;
    const handleScroll = () => {
      const distanceFromBottom = scrollNode.scrollHeight - scrollNode.scrollTop - scrollNode.clientHeight;
      pinnedRef.current = distanceFromBottom <= threshold;
    };
    scrollNode.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollNode.removeEventListener("scroll", handleScroll);
  }, [scrollNode, threshold]);

  useEffect(() => {
    if (!scrollNode || !contentNode) return;
    const follow = () => {
      if (pinnedRef.current) scrollNode.scrollTop = scrollNode.scrollHeight;
    };
    follow();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(follow);
    observer.observe(contentNode);
    observer.observe(scrollNode);
    return () => observer.disconnect();
  }, [scrollNode, contentNode]);

  const scrollToBottom = useCallback(() => {
    if (!scrollNode) return;
    pinnedRef.current = true;
    scrollNode.scrollTop = scrollNode.scrollHeight;
  }, [scrollNode]);

  return { scrollRef: setScrollNode, contentRef: setContentNode, scrollToBottom };
};
